/**
 * ThemedInput — RTL-aware input wrapper.
 * Direction is read from document.documentElement.dir directly — no prop, no fallback.
 * @layer-1 — always use inside FormField for label + error + helperText support.
 */
import React, { forwardRef } from "react";
import { getEffectiveDir } from "@/shared/lib/direction";
import { Input as ShadcnInput } from "@/components/ui/input";
import { cn } from "@/shared/lib/utils";

interface ThemedInputProps extends Omit<React.ComponentPropsWithoutRef<"input">, "prefix"> {
  /**
   * Optional non-interactive leading addon (e.g. a currency `$` cell). When set,
   * the border moves to the wrapper so the addon and the input read as one
   * unified field; the input itself becomes borderless.
   */
  prefix?: React.ReactNode;
  /** Optional interactive element (e.g., password toggle) rendered inside input */
  suffix?: React.ReactNode;
  /**
   * Suffix position:
   * - 'end' (default): flips with direction — start in RTL, end in LTR. Use for directional icons.
   * - 'physical': always on physical right. Use for non-directional icons (Eye, EyeOff).
   */
  suffixPosition?: 'end' | 'physical';
  /** Show error border state (e.g. red border for validation errors) */
  error?: boolean;
}

const ThemedInput = forwardRef<HTMLInputElement, ThemedInputProps>(
  ({ prefix, suffix, suffixPosition = 'end', error = false, className, style, dir: _dir, ...props }, ref) => {
    const effectiveDir = getEffectiveDir();
    const isRTL = effectiveDir === "rtl";

    const themedStyle = {
      "--input-focus-color": "var(--foreground)",
      "--input-hover-color": "var(--foreground)",
      ...style,
    } as React.CSSProperties;

    /**
     * Prefix variant: the border lives on the wrapper (with `overflow-hidden` so
     * the addon clips to the pill radius), making the leading cell + input one
     * seamless field. The input is borderless; focus/hover surface via
     * `focus-within` on the wrapper.
     */
    if (prefix) {
      return (
        <div
          dir={effectiveDir}
          className={cn(
            "flex items-stretch overflow-hidden rounded-[var(--radius-input)]",
            "border bg-[var(--input-bg)] transition-colors duration-150",
            error ? "border-[var(--color-error)]" : "border-[var(--input-border)]",
            "hover:border-[var(--foreground)] focus-within:border-[var(--foreground)]",
          )}
        >
          {prefix}
          <ShadcnInput
            ref={ref}
            className={cn(
              "min-w-0 flex-1 h-auto py-[11px] ps-3 pe-5 text-[13.5px] text-[var(--foreground)]",
              "border-none bg-transparent rounded-none shadow-none",
              "focus-visible:ring-0 focus-visible:border-none",
              "placeholder:text-[var(--text-tertiary)]",
              className,
            )}
            style={themedStyle}
            {...props}
          />
        </div>
      );
    }

    const suffixWithTabIndex = suffix && React.isValidElement(suffix)
      ? React.cloneElement(suffix as React.ReactElement<{ tabIndex?: number }>, { tabIndex: -1 })
      : suffix;

    return (
      <div dir={effectiveDir} className="relative">
        <ShadcnInput
          ref={ref}
          className={cn(
            "[&:focus-visible]:!border-[var(--input-focus-color)] [&:focus-visible]:ring-0",
            "[&:hover]:!border-[var(--input-hover-color)]",
            /* Design spec: 20px pill radius, 11px vertical / 20px horizontal padding */
            "rounded-[var(--radius-input)] h-auto py-[11px] px-5 text-[13.5px] text-[var(--foreground)]",
            error
              ? "border border-[var(--color-error)]"
              : "border border-[var(--input-border)]",
            "bg-[var(--input-bg)]",
            "placeholder:text-[var(--text-tertiary)]",
            "transition-colors duration-150",
            className
          )}
          style={themedStyle}
          {...props}
        />
        {suffixWithTabIndex && (
          <div
            className={cn(
              "absolute inset-y-0 z-20",
              /* physical = icon follows text direction (Eye/EyeOff flip like arrows in RTL) */
              /* end = direction-aware logical end: LEFT in RTL, RIGHT in LTR ✓ */
              suffixPosition === 'physical'
                ? "end-0 pe-3"
                : isRTL ? "start-0 ps-3" : "end-0 pe-3"
            )}
          >
            <div className="flex items-center h-full">
              {suffixWithTabIndex}
            </div>
          </div>
        )}
      </div>
    );
  }
);
ThemedInput.displayName = "ThemedInput";

export { ThemedInput };
